import React, { useLayoutEffect, useRef, useState } from "react";
import InvoicePreview from "./InvoicePreview";
import MealPlanPreview from "./MealPlanPreview";
import { LoadPhase, Skeleton, SkeletonCard } from "../../../components/ui";
import { PAGE_HEIGHT, PAGE_WIDTH } from "../previewPage";
import { BrandDetails, BrandPreviewId } from "../types/brand.types";

const DOCUMENTS: Array<{ id: BrandPreviewId; label: string }> = [
  { id: "invoice", label: "Invoice" },
  { id: "meal-plan", label: "Meal plan" },
];

/**
 * The documents are laid out at one canonical page size — `previewPage` —
 * and scaled to whatever width the column has. Type, rules and spacing all
 * grow together, so a wider panel is a bigger page rather than the same page
 * with more whitespace in it.
 *
 * What the frame spends on itself around the page: the stack's scrollbar,
 * which stands beside the sheets rather than over them, and its own border
 * either side. Reserved whether or not the open document has more than one
 * sheet, so switching to the meal plan does not move the panel.
 */
const SCROLLBAR = 8;
const FRAME_BORDER = 2;
const FRAME_CHROME = SCROLLBAR + FRAME_BORDER;

/** Tailwind's `lg` — the width at which the panel becomes a fixed column. */
const LG_BREAKPOINT = 1024;

interface BrandPreviewPanelProps {
  details: BrandDetails;
  active: BrandPreviewId;
  onChange: (id: BrandPreviewId) => void;
  /**
   * Where the form's two records are. The document is drawn from specimen
   * values and could open the instant the panel mounts — but a page whose two
   * columns arrive one after the other reads as the screen arriving twice, so
   * it waits for the form and the pair land together.
   */
  phase?: LoadPhase;
}

/**
 * Live preview of what the form produces. The two documents share one frame
 * so switching between them never moves the panel.
 */
const BrandPreviewPanel: React.FC<BrandPreviewPanelProps> = ({
  details,
  active,
  onChange,
  phase = "ready",
}) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  /** The open document's own height, before the transform below shrinks it. */
  const [documentHeight, setDocumentHeight] = useState(PAGE_HEIGHT);

  // The page is sized to whichever axis of the panel runs out first, so it is
  // always shown whole — never scrolled and never cropped.
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    const measure = () => {
      // Below `lg` the panel is not height-bound — it sits under the form and
      // takes whatever height the page needs — so only its width limits it.
      const heightBound = window.innerWidth >= LG_BREAKPOINT;

      // Fractional, unlike clientWidth/Height: rounding the box up by half a
      // pixel is enough to clip the page's bottom edge.
      const { width, height } = box.getBoundingClientRect();

      // From `lg` the column is sized by this box, so its width can't also be
      // what sizes the box. The ceiling comes from the `max-w-*` classes
      // below, which keeps the breakpoint sizes in the stylesheet.
      const ceiling = parseFloat(window.getComputedStyle(box).maxWidth);
      const room = heightBound ? ceiling : width;

      // The page has what is left once the frame has taken its own chrome —
      // left out of the sum, the sheet is wider than the room inside the
      // border and its right edge is clipped.
      const byWidth = (room - FRAME_CHROME) / PAGE_WIDTH;
      const byHeight = heightBound
        ? (height - FRAME_BORDER) / PAGE_HEIGHT
        : Infinity;

      const next = Math.min(byWidth, byHeight);
      setScale(next > 0 && Number.isFinite(next) ? next : 1);
    };

    measure();
    window.addEventListener("resize", measure);

    // A window resize is not the only thing that changes the box: the web
    // font swapping in after first paint re-flows the header above it. A
    // measurement taken before that leaves the page taller than its box, and
    // the overflow is what crops the bottom of the document.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(box);

    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, []);

  const waiting = phase !== "ready";

  // How tall the open document is — one sheet for the invoice, the whole
  // stack for the meal plan, which is not known until its closing documents
  // have been split into sheets. Read off the layout, which a transform does
  // not touch, and given to the box around it below: a scroller left to work
  // out for itself how much room a transformed stack takes leaves a band of
  // nothing under the last sheet.
  useLayoutEffect(() => {
    const stack = documentRef.current;
    if (!stack) return;

    const read = () => setDocumentHeight(stack.offsetHeight || PAGE_HEIGHT);

    read();
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(read);
    observer?.observe(stack);
    return () => observer?.disconnect();
  }, [active, waiting]);

  /** The frame's footprint — one whole page and the chrome around it, which
   *  the block standing in for it takes too, so the document lands in the
   *  space its own placeholder held. */
  const frame = {
    width: PAGE_WIDTH * scale + FRAME_CHROME,
    height: PAGE_HEIGHT * scale + FRAME_BORDER,
  };

  /** The meal plan is a document of several sheets — the cover, a sheet per
   *  meal, and the closing Guidelines and Policy — so the frame holds one
   *  page and the stack is turned inside it. The invoice is one sheet and
   *  never scrolls. */
  const multipage = active === "meal-plan";

  return (
    <div className="flex flex-col lg:h-full lg:min-h-0">
      {/* The same pill group the Billings tab switches its two screens with:
          one rounded track holding both halves, and only the selected one
          tinted.

          Row plus margin comes to 40px, the same as the form's section
          captions opposite, which is what lines the two columns' cards up
          with each other. Inside that the pills are taller than the row and
          overflow it evenly top and bottom, so the space left above them
          equals `mb-5` less that overflow, and the space below is the same
          figure — the switcher sits with equal air on either side whatever
          height the pills grow to.

          It is a switch between two documents neither of which is on screen
          yet, so while the panel waits its own block stands here instead. */}
      <div
        role="group"
        aria-label="Preview document"
        className="mb-5 flex h-5 items-center justify-end"
      >
        {waiting ? (
          phase === "slow" && <Skeleton className="h-9 w-[180px] rounded-full" />
        ) : (
          <div className="inline-flex items-center gap-1 rounded-full border border-line bg-white p-1">
            {DOCUMENTS.map((doc) => {
              const selected = doc.id === active;
              return (
                <button
                  key={doc.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onChange(doc.id)}
                  className={`rounded-full px-3.5 py-1.5 text-[13px] 3xl:text-[14px] transition-colors ${
                    selected
                      ? "bg-[#BFF4FB] font-medium text-[#007E8C]"
                      : "text-ink-muted hover:text-ink"
                  }`}
                >
                  {doc.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* The measured box is what the panel has to spend; the frame below is
          sized to fit inside it, so the panel itself never scrolls — a
          document of several sheets is turned inside its own frame. It is
          measured whether or not the document is in it yet, so the block
          below is already the size the frame will be. */}
      <div
        ref={boxRef}
        className="lg:flex-1 lg:min-h-0 flex justify-end overflow-hidden lg:max-w-[400px] xl:max-w-[440px] 2xl:max-w-[480px] 3xl:max-w-[540px] 4xl:max-w-[620px]"
      >
        {waiting ? (
          // One block for the whole document, not for the rules and rows that
          // will be printed on it.
          <SkeletonCard phase={phase} style={frame} />
        ) : (
          <div
            // The overflow is set here rather than in a class: the scrollbar's
            // own rule sets the shorthand, and a `overflow-x` utility beside
            // it would be at the mercy of which stylesheet came last.
            style={{
              ...frame,
              overflowX: "hidden",
              overflowY: multipage ? "auto" : "hidden",
            }}
            className="rounded-md border border-line bg-white custom-scrollbar-overlay"
          >
            {/* What the document takes once it is scaled, and the only thing
                the scroller above measures: a transform moves no layout, so
                without this the stack is scrolled as though it were still
                full size and a band of nothing follows the last sheet. */}
            <div style={{ height: documentHeight * scale, overflow: "hidden" }}>
              <div
                ref={documentRef}
                style={{
                  width: PAGE_WIDTH,
                  // The meal plan is as tall as its own stack of sheets. The
                  // invoice is one sheet and is given its height, because the
                  // page inside it fills whatever it is put in.
                  height: multipage ? undefined : PAGE_HEIGHT,
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                }}
              >
                {active === "invoice" ? (
                  <InvoicePreview details={details} />
                ) : (
                  <MealPlanPreview details={details} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandPreviewPanel;
